<template>
  <div ref="divRef" class="time-series-data">
    <slot name="header-section" :performanceInfo="performanceInfo" />
    <div class="filter-bar">
      <div class="drop-downs">
        <el-select
          v-model="selectedDevice"
          @input="handleSelectedDevice"
          placeholder="Select Device"
        >
          <el-option
            v-for="item of deviceOptions"
            :key="item.value"
            :label="item.label"
            :value="item.value"
          />
        </el-select>
        <el-select
          class="metrics-width-limit"
          v-model="selectedMetrics"
          multiple
          placeholder="Select Metrics"
          collapse-tags
          :loading="loading"
        >
          <el-option
            v-for="item of metricOptions"
            :key="item.value"
            :label="item.label"
            :value="item.value"
          />
        </el-select>
      </div>
      <div class="buttons-panel gap-2">
        <span>{{ computedSelectIntervalText }}</span>
        <el-popover
          ref="popoverRef"
          placement="left"
          v-model="showAbsoluteTimeRange"
          trigger="manual"
        >
          <AbsoluteTimeRangeView
            v-clickoutside="handleClickOutside"
            @confirm="handleConfirmAbsoluteTimeRange"
            @cancel="showAbsoluteTimeRange = false"
            @resize="handlePopoverResize"
            @reset="handleResetChartRangeType"
          />
          <el-tooltip
            slot="reference"
            content="Absolute time range"
            placement="top"
          >
            <el-button
              icon="el-icon-date"
              :type="
                chartRangeType === ChartRangeType.ABSOLUTE
                  ? 'primary'
                  : 'default'
              "
              circle
              @click="handleShowAbsoluteTimeRange"
            />
          </el-tooltip>
        </el-popover>
        <template v-if="chartRangeType === ChartRangeType.RELATIVE">
          <div>
            <el-tooltip content="Zoom In" placement="top">
              <el-button icon="el-icon-zoom-in" circle @click="onClickZoomIn" />
            </el-tooltip>
          </div>
          <div>
            <el-tooltip content="Zoom Out" placement="top">
              <el-button
                icon="el-icon-zoom-out"
                circle
                @click="onClickZoomOut"
              />
            </el-tooltip>
          </div>
          <div>
            <el-tooltip content="Refresh" placement="top">
              <el-button icon="el-icon-refresh" circle @click="onRefresh" />
            </el-tooltip>
          </div>
        </template>
      </div>
    </div>
    <div class="chart-panel">
      <v-chart
        class="absolute inset-0 overflow-hidden"
        ref="vChartRef"
        :option="option"
        @dataZoom="handleDataZoom"
      />
      <slot name="buttons"></slot>
    </div>
  </div>
</template>

<script>
import { getAllAssetVo, getDashboardChartDeviceDataV2 } from "@/api.js";
import { use } from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";
import { LineChart, ScatterChart } from "echarts/charts";
import VChart from "vue-echarts";
import { formatTimestamp } from "@/utils/timeUtil.js";
import { EventBus } from "@/event-bus";
import { delay } from "@/utils/timeUtil.js";
import {
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  GridComponent,
  ToolboxComponent,
  DataZoomComponent,
} from "echarts/components";
import { globalEventLogTable } from "@/store/modules/eventLog";
import AbsoluteTimeRangeView from "./AbsoluteTimeRangeView.vue";
import dayjs from "dayjs";
import getTimeInterval from "./getTimeInterval";

use([
  CanvasRenderer,
  LineChart,
  ScatterChart,
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  GridComponent,
  ToolboxComponent,
  DataZoomComponent,
]);

const ChartRangeType = {
  RELATIVE: "relative",
  ABSOLUTE: "absolute",
};

/**
 * ![design1](./TimeSeriesDataDesign1.png)
 * 粒度与区间说明:
 * | 粒度 | 展示时间区间（视口） | 点数/间隔          | 左右预加载区间建议               |
 * |------|----------------------|--------------------|----------------------------------|
 * | 1d   | 24小时               | 每5分钟1点，共288点 | 向左24h（历史）、向右无（或等待） |
 * | 1h   | 1小时                | 每15秒，共240点    | 左右各1小时                      |
 * | 1m   | 1分钟                | 每1秒，共60点      | 左右各1分钟                      |
 * | 1s   | 10秒                 | 每100ms，共100点   | 左右各10秒                       |
 * | 1w   | 7天                  | 每小时，共168点    | 左右各1周                        |
 * | 1y   | 1年                  | 每月，共12点       | 左右各1年                        |
 * 1s,10s,1min,10min,1h,6h,1d,1w,1月 全了
 */
const COMMON_CONFIG = {
  "1s": {
    duration: 5 * 60 * 1000, // 5分钟窗口
    maxPoints: { low: 150, medium: 300, high: 450, ultra: 600, full: Infinity },
    interval: "1s", // 1秒间隔
    label: "1秒",
    description: "实时监控 - 快速响应",
    useCase: "设备状态监控、异常检测",
  },
  "10s": {
    duration: 30 * 60 * 1000, // 30分钟窗口
    maxPoints: { low: 90, medium: 180, high: 270, ultra: 360, full: Infinity },
    interval: "10s", // 10秒间隔
    label: "10秒",
    description: "近期数据 - 平衡细节和性能",
    useCase: "趋势分析、运行状态评估",
  },
  "1min": {
    duration: 4 * 60 * 60 * 1000, // 4小时窗口
    maxPoints: { low: 120, medium: 240, high: 360, ultra: 480, full: Infinity },
    interval: "1m", // 1分钟间隔
    label: "1分钟",
    description: "短期历史 - 保留关键特征",
    useCase: "班次数据回顾、设备性能分析",
  },
  "10min": {
    duration: 24 * 60 * 60 * 1000, // 24小时窗口
    maxPoints: { low: 72, medium: 144, high: 216, ultra: 288, full: Infinity },
    interval: "10m", // 10分钟间隔
    label: "10分钟",
    description: "日度数据 - 运行模式分析",
    useCase: "日常运行分析、周期性检查",
  },
  "1h": {
    duration: 7 * 24 * 60 * 60 * 1000, // 7天窗口
    maxPoints: { low: 84, medium: 168, high: 252, ultra: 336, full: Infinity },
    interval: "1h", // 1小时间隔
    label: "1小时",
    description: "周度历史 - 长期趋势",
    useCase: "设备健康趋势、维护计划",
  },
  "6h": {
    duration: 30 * 24 * 60 * 60 * 1000, // 30天窗口
    maxPoints: { low: 60, medium: 120, high: 180, ultra: 240, full: Infinity },
    interval: "6h", // 6小时间隔
    label: "6小时",
    description: "月度历史 - 统计分析",
    useCase: "月度报告、性能评估",
  },
  "1d": {
    duration: 365 * 24 * 60 * 60 * 1000, // 1年窗口
    maxPoints: { low: 183, medium: 365, high: 548, ultra: 730, full: Infinity },
    interval: "1d", // 1天间隔
    label: "1天",
    description: "年度历史 - 长期档案",
    useCase: "年度分析、设备寿命评估",
  },
  "1w": {
    duration: 2 * 365 * 24 * 60 * 60 * 1000, // 2年窗口
    maxPoints: { low: 104, medium: 208, high: 312, ultra: 416, full: Infinity },
    interval: "1w", // 1周间隔
    label: "1周",
    description: "多年度历史 - 周期性趋势",
    useCase: "年度周期分析、长期维护",
  },
  "1mo": {
    duration: 10 * 365 * 24 * 60 * 60 * 1000, // 10年窗口
    maxPoints: { low: 120, medium: 240, high: 360, ultra: 480, full: Infinity },
    interval: "1mo", // 1月间隔（约30天）
    label: "1月",
    description: "超长期历史 - 月度档案",
    useCase: "生命周期分析、长期趋势",
  },
};

const DENSITY_CONFIG = {
  low: {
    label: "Low Density",
    description: "Suitable for low-performance devices",
  }, // 低密度
  medium: {
    label: "Medium Density",
    description: "Balanced performance and detail",
  }, // 中密度
  high: { label: "High Density", description: "More data details" }, // 高密度
  ultra: { label: "Ultra Density", description: "Maximum data density" }, // 超高密度
  full: { label: "Full Density", description: "Show all points" }, // 显示全部点
};

const METRIC_UNIT_MAP = {
  temperature: "°C",
  "temperature:float": "°C",
  humidity: "%",
  "humidity:float": "%",
  vibration: "g",
  pm1: "μg/m³",
  "pm1:int": "μg/m³",
  pm25: "μg/m³",
  "pm25:int": "μg/m³",
  pm4: "μg/m³",
  "pm4:int": "μg/m³",
  pm10: "μg/m³",
  "pm10:int": "μg/m³",
  co: "ppm",
  "co:int": "ppm",
  co2: "ppm",
  "co2:int": "ppm",
  ozone: "ppm",
  "ozone:int": "ppb",
  no2: "ppm",
  "no2:float": "ppm",
  airPressure: "hPa",
  "airPressure:float": "hPa",
  tvoc: "mg/m³",
  "tvoc:int": "mg/m³",
  virusIndex: "",
  "virusIndex:int": "",
  device_key: "",
};

let SELECTED_DEVICE = null;

export default {
  name: "TimeSeriesData",
  components: {
    VChart,
    AbsoluteTimeRangeView,
  },
  emits: ["device-selected"],
  props: {
    device: String, // 用于 v-model 绑定 selectedDevice
    metrics: {
      type: Array,
      default: () => [],
    },
    interval: {
      type: String,
      default: "6h",
    },
  },
  data() {
    return {
      loading: false,
      chartRangeType: ChartRangeType.RELATIVE,
      showAbsoluteTimeRange: false,
      absoluteTimeRange: {
        start: "",
        end: "",
      },
      performanceInfo: {
        points: 0,
        renderTime: 0,
        totalPoints: 0,
      },
      selectedDevice: this.device,
      selectedMetrics: this.metrics.length ? this.metrics : [],
      selectInterval: this.interval || "6h", // 6小时
      selectedDensity: "full", // 默认全密度
      dataZoom: {
        //光标位置
        start: 0,
        end: 100,
      },
      /**
       * @type {DashboardChartDeviceDataResponse[]}
       * 设备仪表盘图表的数据响应数组
       */
      deviceHistory: [], //
      deviceOptions: [],
      metricOptions: [],
      allMetricSet: [], // 缓存所有历史字段（用数组，避免Set响应式异常）
      // 折线图配置
      option: {
        grid: {
          left: 70,
          right: 20,
          top: 60,
          bottom: 40,
        },
        animation: false,
        toolbox: {
          // itemSize: 25,
          // iconStyle: {
          //   color: '#fff'
          // },
          feature: {
            dataZoom: {
              icon: {
                zoom: "none",
                back: "none",
              },
              yAxisIndex: "none",
            },
            // restore: {}, // 重置为初始状态
            // saveAsImage: {} // 保存为图片
          },
        },
        dataZoom: [
          {
            type: "inside", // 区域缩放，鼠标拖拽/滚轮即可缩放
            xAxisIndex: 0, // 针对第一个 x 轴
            zoomOnMouseWheel: false, // 禁用滚轮缩放
            moveOnMouseWheel: true, // 允许滚轮平移（如不需要可设为 false）
          },
          {
            show: false,
            type: "slider", // 显示底部滚动条
            xAxisIndex: 0,
            zoomLock: true, // 禁止缩放，只能拖动
          },
        ],
        // title: {
        //   text: '设备数据趋势',
        //   textStyle: {
        //     color: '#fff'
        //   }
        // },
        tooltip: {
          show: true,
          trigger: "axis",
          axisPointer: {
            type: "cross", // 显示十字准星，x/y轴都有线
            label: {
              backgroundColor: "#6a7985",
            },
          },
          textStyle: {
            color: "#000",
          },
          formatter(params) {
            if (!params || !params.length) return "";
            let html = "";
            params.forEach((item) => {
              if (item.seriesName === "Alerts") {
                // 查找报警内容
                const alert = (globalEventLogTable.state.tableData || []).find(
                  (a) =>
                    a.timestamp === item.value[0] &&
                    a.deviceKey === SELECTED_DEVICE
                );
                html += `<span style="color:#ff0000;">${alert.name}</span><br/>`;
                html += `time: ${formatTimestamp(item.value[0])}<br/>`;
                if (alert && alert.event) {
                  html += `content: ${alert.event}<br/>`;
                }
              } else {
                // 普通指标
                let val =
                  item.value !== null &&
                  item.value !== undefined &&
                  !isNaN(item.value[1])
                    ? Number(item.value[1]).toFixed(
                        8
                      ) /*202508011553王亮说精度高点*/
                    : "-";
                const metricKey = item.seriesName.toLowerCase();
                const unit = METRIC_UNIT_MAP[metricKey] || "";
                html += `<span style="display:inline-block;margin-right:5px;border-radius:10px;width:9px;height:9px;background:${
                  item.color
                };"></span>${item.seriesName}: <b>${val}</b>${
                  unit ? " " + unit : ""
                }<br/>`;
              }
            });
            return html;
          },
        },
        legend: {
          data: [],
          textStyle: {
            color: "#fff",
          },
          top: 24, // 距离顶部24px，可根据需要调整
          // orient: 'horizontal',
          // bottom: 0
        },
        xAxis: {
          type: "time",
          axisLabel: {
            color: "#fff",
            // inside: true,
          },
          axisLine: {
            lineStyle: {
              color: "#fff",
            },
          },
          // boundaryGap: false // 让线贴边
        },
        yAxis: {
          type: "value",
          axisLabel: {
            // inside: true,
            color: "#fff",
            formatter: function (value) {
              // 超过一千万用科学计数法
              if (Math.abs(value) >= 1e7) {
                return value.toExponential(2);
              }
              // 其它情况保留两位小数
              return Number(value).toFixed(2);
            },
          },
          axisLine: {
            lineStyle: {
              color: "#fff",
            },
          },
          splitLine: {
            lineStyle: {
              color: "#444",
            },
          },
        },
        series: [],
      },
    };
  },
  watch: {
    selectedMetrics() {
      // 当选中的指标变化时，重新计算图表配置
      this.updateChartOption();
    },
    async selectedDensity() {
      // 当选中的密度变化时，重新获取数据
      await this.fetchChartData();
    },
    loading(val) {
      if (val) {
        this.$refs.vChartRef?.chart?.showLoading("default", {
          maskColor: "rgba(0,0,0,0)", // 透明遮罩
          text: "loading", // 可选：不显示文字
        });
      } else {
        this.$refs.vChartRef?.chart?.hideLoading();
      }
    },
  },
  computed: {
    ChartRangeType: () => ChartRangeType,
    COMMON_CONFIG() {
      return COMMON_CONFIG;
    },
    DENSITY_CONFIG() {
      return DENSITY_CONFIG;
    },
    /**
     * 获取当前配置
     */
    currentConfig() {
      return (
        this.COMMON_CONFIG[this.selectInterval] ?? this.COMMON_CONFIG["1d"]
      );
    },
    /**
     * 友好显示当前时间间隔
     */
    computedSelectIntervalText() {
      // 通用时间单位映射
      const unitMap = {
        s: "second",
        m: "minute",
        h: "hour",
        d: "day",
        w: "week",
        mo: "month",
      };
      const result = /^(\d+)([a-zA-Z]+)$/.exec(this.selectInterval);
      if (result && unitMap[result[2]]) {
        return `Time Interval: ${result[1]} ${unitMap[result[2]]}`;
      }
      return `Time Interval: ${this.selectInterval}`;
    },
    viewportTimeRange() {
      return {
        start: Date.now() - this.currentConfig.duration,
        end: Date.now(),
      };
    },
    eventLogList() {
      return this.$store.getters["eventLog/list"] || [];
    },
  },
  methods: {
    getAllAssetVo,
    handlePopoverResize() {
      this.$nextTick(() => {
        this.$refs.popoverRef.updatePopper();
      });
    },
    async handleSelectedDevice(val) {
      // 切换设备时重置所有指标缓存
      this.allMetricSet = [];
      // 当选中的设备变化时，重新获取数据
      SELECTED_DEVICE = val;
      await this.fetchChartData();
      this.selectDefaultMetric();
      this.updateChartOption();
      this.$emit("device-selected", val);
    },
    handleShowAbsoluteTimeRange() {
      this.showAbsoluteTimeRange = true;
    },
    handleConfirmAbsoluteTimeRange(range) {
      const me = this;
      me.showAbsoluteTimeRange = false;
      me.chartRangeType = ChartRangeType.ABSOLUTE;
      if (range && range.start != "" && range.end != "") {
        me.absoluteTimeRange = range;
        const interval = range.interval.replace("min", "m");
        me.fetchAbsoluteTimeRangeData(range.start, range.end, interval);
      }
    },
    handleResetChartRangeType() {
      this.showAbsoluteTimeRange = false;
      this.chartRangeType = ChartRangeType.RELATIVE;
      this.selectInterval = "1d"; // 重置为默认粒度
      this.fetchChartData();
    },
    handleChartMouseDown() {
      // 你的逻辑，比如隐藏 tooltip
      this.option = {
        ...this.option,
        tooltip: {
          ...this.option.tooltip,
          show: false,
        },
      };
    },
    handleChartMouseUp() {
      // 你的逻辑，比如恢复 tooltip
      this.option = {
        ...this.option,
        tooltip: {
          ...this.option.tooltip,
          show: true,
        },
      };
    },
    handleOpenZoom() {
      this.$refs.vChartRef?.chart?.dispatchAction({
        type: "takeGlobalCursor",
        key: "dataZoomSelect",
        dataZoomSelectActive: true,
      });
    },
    setSliderZoom(start, end) {
      this.$refs.vChartRef?.chart?.dispatchAction({
        type: "dataZoom",
        start,
        end,
        startValue: 50,
        endValue: 100,
      });
    },
    async onClickZoomIn() {
      const intervalKeys = Object.keys(this.COMMON_CONFIG);
      const currentIndex = intervalKeys.indexOf(this.selectInterval);
      if (currentIndex > 0) {
        this.selectInterval = intervalKeys[currentIndex - 1];
      }
      // 重新请求数据
      await this.fetchChartData();
    },
    async onClickZoomOut() {
      const intervalKeys = Object.keys(this.COMMON_CONFIG);
      const currentIndex = intervalKeys.indexOf(this.selectInterval);
      if (currentIndex < intervalKeys.length - 1 && currentIndex !== -1) {
        this.selectInterval = intervalKeys[currentIndex + 1];
      }
      // 重新请求数据
      await this.fetchChartData();
    },
    /**
     * 下拉框选项获取
     */
    async fetchDeviceOptions() {
      const devices = await getAllAssetVo();
      // 模拟获取设备选项
      this.deviceOptions = devices.map((device) => ({
        value: device.assetId,
        // value: device.deviceKey, // 假设设备ID是唯一标识
        label: device.name,
      }));
      // 如果没有选中设备，默认选中第一个
      if (!this.selectedDevice && this.deviceOptions.length) {
        this.selectedDevice = this.deviceOptions[0].value;
        SELECTED_DEVICE = this.selectedDevice;
        this.$emit("device-selected", this.selectedDevice);
      }
    },
    /**
     * 补齐数据点，保证每个时间点都有数据
     * @param {Array} data 服务器返回的数据 [{timestamp, payload}]
     * @param {number} startTime 起始时间戳
     * @param {number} endTime 结束时间戳
     * @param {number} step 间隔（毫秒）
     * @returns {Array} 补齐后的数据
     */
    fillMissingData(data, startTime, endTime, step) {
      // 先按时间升序排序
      const sorted = [...data].sort((a, b) => a.timestamp - b.timestamp);
      let dataIdx = 0;
      let lastPayload = {};
      const filled = [];
      const now = Date.now();
      for (let t = startTime; t <= endTime; t += step) {
        // 如果时间点大于当前时间，payload 设为 {}
        if (t > now) {
          filled.push({
            timestamp: t,
            payload: {},
          });
          continue;
        }
        // 找到不大于当前t的最新payload
        while (dataIdx < sorted.length && sorted[dataIdx].timestamp <= t) {
          lastPayload = sorted[dataIdx].payload;
          dataIdx++;
        }
        filled.push({
          timestamp: t,
          payload: { ...lastPayload },
        });
      }
      return filled;
    },
    /**
     * 通用采样转换方法
     * @param {Array} data 原始数据数组
     * @param {number} maxPoints 采样后最大点数
     * @returns {Array} 采样后的数据
     */
    sampleDataPoints(data, maxPoints) {
      if (maxPoints === Infinity) return data; // 如果 maxPoints 为 Infinity，直接返回原数据
      if (!Array.isArray(data) || data.length <= maxPoints) return data;
      const ratio = Math.ceil(data.length / maxPoints);
      return data.filter((_, idx) => idx % ratio === 0);
    },
    /**1s,2s,5s,10s,30s,1m,2m,5m,10m,30m,1h,2h,1d */
    intervalToMs(interval) {
      const match = /^(\d+)([a-zA-Z]+)$/.exec(interval);
      if (match) {
        const value = parseInt(match[1], 10);
        const unit = match[2];
        const unitMap = {
          s: 1000,
          m: 60000,
          h: 3600000,
          d: 86400000,
          w: 604800000,
          mo: 2592000000,
        };
        if (unitMap[unit]) {
          return value * unitMap[unit];
        }
      }
      return 60000; // 默认1分钟
    },
    /**
     * 前端模拟生成时序数据，替代 getDashboardChartDeviceData
     * @param {Object} params
     *   - assetId
     *   - startTime
     *   - endTime
     *   - interval (如 '1m', '10m', '1h' 等)
     * @returns {Promise<Array<{timestamp: number, payload: Object}>>}
     */
    async mockDashboardChartDeviceData({
      assetId,
      startTime,
      endTime,
      interval,
    }) {
      // 支持的指标
      const metrics = [
        "temperature",
        "humidity",
        "vibration",
        "pm1:int",
        "humidity:float",
        "co:int",
        "co2",
        "ozone",
        "no2",
        "pm1",
        "pm25:int",
        "pm4",
        "virusIndex:int",
        "airPressure:float",
        "tvoc",
        "device_key",
        "airPressure",
        "pm10",
        "tvoc:int",
        "co",
        "co2:int",
        "pm25",
        "virusIndex",
        "temperature:float",
        "no2:float",
        "pm10:int",
        "pm4:int",
        "ozone:int",
      ];
      // 间隔转毫秒
      // 通用的时间间隔单位支持 s, m, h, d, w, mo
      let step = 60000; // 默认1分钟

      step = this.intervalToMs(interval);
      const data = [];
      for (let t = startTime; t <= endTime; t += step) {
        const payload = {};
        metrics.forEach((metric) => {
          // 随机生成不同指标的数据
          if (metric === "temperature")
            payload[metric] = 20 + Math.sin(t / 1e7) * 5 + Math.random();
          if (metric === "humidity")
            payload[metric] = 50 + Math.cos(t / 2e7) * 10 + Math.random() * 2;
          if (metric === "vibration")
            payload[metric] =
              Math.abs(Math.sin(t / 5e6)) * 2 + Math.random() * 0.2;
          if (metric === "pm1:int") payload[metric] = Math.random() * 10;
          if (metric === "humidity:float")
            payload[metric] = 40 + Math.random() * 20;
          if (metric === "co:int")
            payload[metric] = Math.floor(Math.random() * 5);
          if (metric === "co2") payload[metric] = 400 + Math.random() * 200;
          if (metric === "ozone") payload[metric] = Math.random();
          if (metric === "no2") payload[metric] = Math.random();
          if (metric === "pm1") payload[metric] = Math.random() * 10;
          if (metric === "pm25:int")
            payload[metric] = Math.floor(Math.random() * 50);
          if (metric === "pm4") payload[metric] = Math.random() * 10;
          if (metric === "virusIndex:int")
            payload[metric] = Math.floor(Math.random() * 10);
          if (metric === "temperature")
            payload[metric] = 20 + Math.sin(t / 1e7) * 5 + Math.random();
          if (metric === "humidity")
            payload[metric] = 50 + Math.cos(t / 2e7) * 10 + Math.random() * 2;
          if (metric === "airPressure:float")
            payload[metric] = 1000 + Math.random() * 20;
          if (metric === "tvoc") payload[metric] = Math.random() * 2;
          if (metric === "device_key") payload[metric] = assetId;
          if (metric === "airPressure")
            payload[metric] = 1000 + Math.random() * 20;
          if (metric === "pm10") payload[metric] = Math.random() * 20;
          if (metric === "tvoc:int")
            payload[metric] = Math.floor(Math.random() * 10);
          if (metric === "co") payload[metric] = Math.random();
          if (metric === "co2:int")
            payload[metric] = 400 + Math.floor(Math.random() * 200);
          if (metric === "pm25") payload[metric] = Math.random() * 20;
          if (metric === "virusIndex") payload[metric] = Math.random() * 10;
          if (metric === "temperature:float")
            payload[metric] = 20 + Math.random() * 5;
          if (metric === "no2:float") payload[metric] = Math.random();
          if (metric === "pm10:int")
            payload[metric] = Math.floor(Math.random() * 20);
          if (metric === "pm4:int")
            payload[metric] = Math.floor(Math.random() * 10);
          if (metric === "ozone:int")
            payload[metric] = Math.floor(Math.random() * 2);
        });
        data.push({ timestamp: t, payload });
      }
      // await delay(500);
      console.log(
        "Mock data start:",
        formatTimestamp(startTime),
        "end:",
        formatTimestamp(endTime)
      );
      return data;
    },
    /**
     * 在 dataZoom 结束后显示 tooltip
     */
    handleDataZoomEnd() {
      if (this._tooltipTimeout) clearTimeout(this._tooltipTimeout);
      this._tooltipTimeout = setTimeout(() => {
        this.$refs.vChartRef?.chart?.dispatchAction({
          type: "showTip",
        });
      }, 1000);
    },
    async handleDataZoom(args) {
      // // 当数据缩放时触发
      const zoomStart = args.batch?.[0]?.start ?? 0;
      const zoomEnd = args.batch?.[0]?.end ?? 100;

      // 如果缩放到最左侧，加载更多左侧数据
      if (zoomStart === 0) {
        await this.loadMore("left");
      }
      // 如果缩放到最右侧，加载更多右侧数据
      if (zoomEnd === 100) {
        await this.loadMore("right");
      }
    },
    turnOffAnimation() {
      this.option = {
        ...this.option,
        animation: false, // 全局关闭动画
      };
    },
    turnOnAnimation() {
      this.option = {
        ...this.option,
        animation: true, // 全局开启动画
      };
    },
    /**
     * 加载更多数据（左/右方向）
     * @param {'left'|'right'} direction
     */
    async loadMore(direction) {
      const config = this.currentConfig;
      if (direction === "left") {
        // 获得最右边的节点的时间戳
        const currentEndTime =
          this.deviceHistory[this.deviceHistory.length - 1].timestamp;
        // 计算新的加载区间：从当前最左点往左扩展一屏
        const newEndTime = currentEndTime - 1 * config.duration;
        const newStartTime = newEndTime - 2 * config.duration;
        const newData = await this.fetchData(newStartTime, newEndTime);
        // 如果新数据为空，则不更新
        if (newData.length === 0) {
          this.$message.warning("No more history data available");
          return;
        }
        // 新数据拼接到左侧（新数据在左，旧数据在右）
        this.deviceHistory = newData; //保持释放不看的数据
        // 重置dataZoom到新数据区间的末尾，保持视口在原有数据区间
        this.dataZoom = { start: 50, end: 99.9 };
      } else if (direction === "right") {
        // 当前可视区最右侧时间
        const currentEndTime =
          this.deviceHistory[this.deviceHistory.length - 1].timestamp;
        // 计算新的加载区间：往右扩展1倍窗口
        const newStartTime = currentEndTime - config.duration;
        const newEndTime = currentEndTime + 1 * config.duration;

        if (newEndTime > Date.now()) {
          //调用重新加载
          this.onRefresh();
          return;
        }

        const data = await this.fetchData(newStartTime, newEndTime);
        // 如果新数据为空，则不更新
        if (data.length === 0) {
          this.$message.warning("No more new data available");
          return;
        }

        //服务器只返回了几条新数据的处理
        if (
          data[0].timestamp < newStartTime ||
          data[data.length - 1].timestamp > newEndTime
        ) {
          const prevStart = this.dataZoom.start;
          const prevEnd = this.dataZoom.end;
          const prevLen = this.deviceHistory.length;

          // 合并数据，去重并排序
          const merged = [...this.deviceHistory, ...data]
            .filter(
              (item, idx, arr) =>
                idx === 0 || item.timestamp !== arr[idx - 1].timestamp
            )
            .sort((a, b) => a.timestamp - b.timestamp);
          this.deviceHistory = merged;

          // 保持原有视口区间
          const newLen = merged.length;
          const visibleCount = Math.round(
            ((prevEnd - prevStart) / 100) * prevLen
          );
          const newEnd = 100;
          const newStart = Math.max(0, 100 - (visibleCount / newLen) * 100);

          this.dataZoom = {
            start: newStart,
            end: newEnd,
          };
        }
        //用户移动进入了历史数据区间中
        else {
          // 新数据拼接到右侧
          this.deviceHistory = data; //保持释放不看的数据
          // 重置dataZoom到1,51
          this.dataZoom = { start: 0.1, end: 50 };
        }
      }
      // 更新图表配置
      this.updateChartOption();
    },
    async fetchData(startTimeStamp, endTimeStamp) {
      const me = this;
      // 这里可以根据实际需求设置时间范围
      const config = me.currentConfig;
      const interval = me.selectInterval ?? config.interval;
      const performanceStartTime = new Date(); // 记录性能开始时间
      let data = []; // 列表
      // requestId 用于防止异步乱序
      if (!me._requestIdCounter) me._requestIdCounter = 1;
      const requestId = ++me._requestIdCounter;
      me._lastRequestId = requestId;
      me.loading = true;

      const isMockDevice =
        me.deviceOptions.find((i) => i.value == me.selectedDevice)?.label ===
        "Mock Device";

      if (isMockDevice) {
        data = await me.mockDashboardChartDeviceData({
          assetId: me.selectedDevice,
          startTime: startTimeStamp,
          endTime: endTimeStamp,
          interval,
        });
      } else if (
        me.selectedDevice !== null &&
        me.selectedDevice !== undefined &&
        me.selectedDevice !== ""
      ) {
        // 否则调用真实的 API 获取数据
        data = await getDashboardChartDeviceDataV2({
          assetId: me.selectedDevice,
          startTime: startTimeStamp,
          endTime: endTimeStamp,
          interval,
        });
      }
      me.loading = false;

      // 只有最新请求才处理数据
      if (requestId !== me._lastRequestId) {
        return [];
      }

      me.performanceInfo.totalPoints = data.length;

      me.generateMetricOptions(data, requestId);

      // // 采样前补齐
      // const step = this.intervalToMs([interval] || 60000);
      // data = me.fillMissingData(data, startTimeStamp, endTimeStamp, step);

      // // 采样数据
      // const maxPoints =
      //   me.COMMON_CONFIG[me.selectInterval]?.maxPoints?.[me.selectedDensity] ||
      //   300;
      // data = me.sampleDataPoints(data, maxPoints);

      me.performanceInfo.points = data.length;
      me.performanceInfo.renderTime = Date.now() - performanceStartTime; // 重置渲染时间

      me.deviceHistory = data;
      return data;
    },
    /**
     * 根据数据动态生成指标选项
     * @param {Array} data
     */
    generateMetricOptions(data, requestId) {
      // requestId保护，只有最新请求才更新metricOptions
      if (requestId && requestId !== this._lastRequestId) {
        return;
      }
      // 只加不减，data为空不清空
      if (Array.isArray(data) && data.length > 0) {
        data.forEach((item) => {
          if (item.payload && typeof item.payload === "object") {
            Object.keys(item.payload).forEach((key) => {
              if (
                key !== "device_key" &&
                !key.endsWith(":int") &&
                !key.endsWith(":float") &&
                !key.endsWith(":string") &&
                !key.endsWith(":boolean")
              ) {
                if (!this.allMetricSet.includes(key)) {
                  this.allMetricSet.push(key);
                }
              }
            });
          }
        });
      }
      // 只用allMetricSet生成metricOptions
      this.metricOptions = this.allMetricSet.map((key) => ({
        value: key,
        label: key.charAt(0).toUpperCase() + key.slice(1),
      }));
    },
    selectDefaultMetric() {
      if (this.metricOptions.length > 0) {
        this.selectedMetrics = this.metricOptions
          .slice(0, 3)
          .map((m) => m.value); // 默认选前三个指标
      } else {
        this.selectedMetrics = this.metricOptions
          .filter((m) => this.metricOptions.includes(m.value))
          .map((m) => m.value);
      }
    },
    getDeviceAlertsSeries() {
      // 获取报警事件数据列表
      const alerts = (this.eventLogList || []).filter((item) => {
        return item.deviceKey === this.selectedDevice && item.type === "alert";
      });

      // 获取主线数据区间
      if (!this.deviceHistory.length) return [];
      const minTime = this.deviceHistory[0].timestamp;
      const maxTime =
        this.deviceHistory[this.deviceHistory.length - 1].timestamp;

      // 只保留区间内的报警点
      const filteredAlerts = alerts.filter(
        (pt) => pt.timestamp >= minTime && pt.timestamp <= maxTime
      );

      // 计算报警点的 y 轴中线
      let yMiddle = 0;
      const echartsRef = this.$refs.vChartRef?.chart;
      if (echartsRef) {
        const yAxis = echartsRef.getModel().getComponent("yAxis");
        const yMin = yAxis?.axis?.scale?.getExtent?.()[0] ?? 0;
        const yMax = yAxis?.axis?.scale?.getExtent?.()[1] ?? 100;
        yMiddle = (yMin + yMax) / 2;
      } else {
        yMiddle = 50; // fallback
      }

      // 构造报警点 scatter series
      return filteredAlerts.map((pt) => [pt.timestamp, yMiddle]);
    },
    parseDateRangeKey(key) {
      if (!key || typeof key !== "string") return null;
      if (key.startsWith("now-")) {
        const match = key.match(/^now-(\d+)([mhd])$/);
        if (match) {
          const value = parseInt(match[1], 10);
          const unit = match[2];
          let ms = 0;
          if (unit === "m") ms = value * 60 * 1000;
          if (unit === "h") ms = value * 60 * 60 * 1000;
          if (unit === "d") ms = value * 24 * 60 * 60 * 1000;
          return Date.now() - ms;
        }
      }
      return null;
    },
    async fetchAbsoluteTimeRangeData(startTimeStr, endTimeStr, timeInterval) {
      this.loading = true;
      try {
        let startTimeStamp = "";
        let endTimeStamp = "";

        if (endTimeStr === "now") {
          endTimeStamp = Date.now();
        } else if (endTimeStr) {
          endTimeStamp = dayjs(endTimeStr).valueOf();
        }

        if (
          startTimeStr &&
          typeof startTimeStr === "string" &&
          startTimeStr.startsWith("now-")
        ) {
          startTimeStamp = this.parseDateRangeKey(startTimeStr);
        } else if (startTimeStr) {
          startTimeStamp = dayjs(startTimeStr).valueOf();
        }

        // 优化：根据时间跨度动态选择合适的间隔（复用 getTimeInterval）
        if (timeInterval) {
          this.selectInterval = timeInterval;
        } else {
          const { intervalValue, intervalUnit } = getTimeInterval(
            startTimeStr,
            endTimeStr
          );
          this.selectInterval = `${intervalValue}${intervalUnit}`;
        }

        await this.fetchData(startTimeStamp, endTimeStamp); //加载两屏数据
        // 动态生成指标选项
        this.dataZoom = {
          start: 0,
          end: 100,
        }; //不可以拖动

        this.updateChartOption();
      } catch (e) {
        this.$message.error("Failed to fetch data");
      } finally {
        this.loading = false;
      }
    },
    /**
     * 第一次获取数据
     */
    async fetchChartData() {
      const me = this;
      if (me.chartRangeType === ChartRangeType.ABSOLUTE) {
        const interval = me.absoluteTimeRange?.interval.replace("min", "m");
        await me.fetchAbsoluteTimeRangeData(
          me.absoluteTimeRange.start,
          me.absoluteTimeRange.end,
          interval
        );
      } else {
        await me.fetchRelativeChartData();
      }
    },
    async fetchRelativeChartData() {
      const me = this;
      me.loading = true;
      try {
        // 这里可以根据实际需求设置时间范围
        const { start, end } = me.viewportTimeRange; //一屏的开始结束
        const newStart = start - 1 * me.currentConfig.duration; // 向左额外加1屏数据
        await me.fetchData(newStart, end); //加载两屏数据
        // 动态生成指标选项

        me.dataZoom = {
          start: 50,
          end: 99.9,
        };
        me.updateChartOption();
      } catch (e) {
        me.$message.error("Failed to fetch data");
        console.error(e);
      } finally {
        me.loading = false;
      }
    },
    /**
     * 更新图表配置
     */
    updateChartOption() {
      // 构造 series 数据
      const series = [];
      let allValues = [];

      // 指标
      for (const metric of this.selectedMetrics) {
        const label =
          this.metricOptions.find((m) => m.value === metric)?.label || metric;
        // 每个点是 [timestamp, value]
        const data = this.deviceHistory.map((item) => [
          item.timestamp,
          item.payload[metric] ?? null,
        ]);
        series.push({
          name: label,
          type: "line",
          large: false,
          data,
        });
        // 收集所有有效值
        allValues = allValues.concat(
          data
            .map((d) => d[1])
            .filter((v) => v !== null && v !== undefined && !isNaN(v))
        );
      }

      // 计算 y 轴范围
      let yMin = 0,
        yMax = 100;
      if (allValues.length > 0) {
        yMin = Math.min(...allValues);
        yMax = Math.max(...allValues);
        // 如果最大最小值相等，给一点间距
        if (yMin === yMax) {
          yMin -= 1;
          yMax += 1;
        }
      }

      // 报警点
      const alarmData = this.getDeviceAlertsSeries("time");
      if (alarmData.length > 0) {
        series.push({
          name: "Alerts",
          type: "scatter",
          large: true,
          symbol: "pin",
          symbolSize: 20,
          itemStyle: { color: "#ff0000" },
          data: alarmData,
          z: this.selectedMetrics.length + 1,
        });
      }

      // 更新 option
      this.option = {
        ...this.option,
        xAxis: {
          ...this.option.xAxis,
          type: "time",
          data: undefined,
        },
        yAxis: {
          ...this.option.yAxis,
          min: yMin,
          max: yMax,
        },
        legend: {
          ...this.option.legend,
          data: series.map((s) => s.name),
          selected: { Alerts: true },
        },
        series,
        dataZoom: [
          {
            ...this.option.dataZoom[0],
            start: this.dataZoom.start,
            end: this.dataZoom.end,
          },
          {
            ...this.option.dataZoom[1],
            start: this.dataZoom.start,
            end: this.dataZoom.end,
          },
        ],
      };
    },
    // 刷新逻辑
    async onRefresh() {
      await this.fetchChartData();
    },
    async onTag() {
      const result = await this.$loaderWindow({
        title: "Add Tag",
        component: () => import("./EditTagDialog.vue"),
        width: "500px",
      });
      console.log("Tag result:", result);
    },
    onUpload() {
      // 上传逻辑
      console.log("Uploading data...");
    },
    resizeChart() {
      this.$refs.vChartRef?.chart?.resize();
    },
    handleResize() {
      this.$nextTick(async () => {
        await delay(300); // 确保 DOM 更新后再调整大小
        this.resizeChart();
      });
    },
    async init() {
      const me = this;
      await me.fetchDeviceOptions();
      await me.fetchChartData();
      if (me.$props.metrics.length === 0 && me.metricOptions.length > 0) {
        // 如果没有传入指标，则默认选前三个指标
        me.selectDefaultMetric();
      }
      me.updateChartOption();
      window.addEventListener("resize", me.handleResize);
      EventBus.$on("mainLeftPanelCollapse", this.handleResize);
      me.resizeChart();
    },
    handleClickOutside() {
      this.showAbsoluteTimeRange = false;
    },
  },

  // 组件挂载后自动获取数据
  mounted() {
    this.init();
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.handleResize);
    EventBus.$off("mainLeftPanelCollapse", this.handleResize);
  },
};
</script>
<style lang="scss" scoped>
.time-series-data {
  display: flex;
  flex-direction: column;
}

.time-series-data ::v-deep .vue-echarts-inner canvas {
  cursor: grab;
}

.time-series-data ::v-deep .vue-echarts-inner canvas:active {
  cursor: grabbing;
}

.filter-bar {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  margin-top: 20px;
  position: relative;
}

.filter-bar .drop-downs {
  display: flex;
  flex-direction: row;
  gap: 10px;
}

.filter-bar .buttons-panel {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;
  font-family: "SFMono-Regular", "Menlo", "Monaco", "Consolas",
    "Liberation Mono", "Courier New", monospace;
}

.tag-panel {
  display: flex;
  flex-direction: row;
  justify-content: center;
  margin-top: 10px;
  gap: 8px;
}

.chart-panel {
  flex: 1 1 0;
  margin-top: 20px;
  background-color: #1e2328;
  padding: 20px;
  border-radius: 10px;
  position: relative;
}

.el-select ::v-deep .el-select__tags {
  display: flex;
  flex-direction: row;
}

.el-select.metrics-width-limit {
  width: fit-content !important;
}
.el-select.metrics-width-limit ::v-deep .el-select__tags {
  .el-tag {
    max-width: 110px;
  }
}
</style>
